import Konva from "konva";
import type { ImageNode, RenderNode } from "../domain/renderSpec";
import { clampPlacement } from "../domain/placement";
import type { ImageSlotId, Placement } from "../domain/types";
import { assetDimensions, getAsset } from "../services/assets";

export interface RendererCallbacks {
  onPlacementChanged?: (slot: ImageSlotId, placement: Placement) => void;
  onTextSlotTapped?: (slot: "viewers" | "telop") => void;
  onImageSelected?: (slot: ImageSlotId) => void;
}

// 描画仕様（RenderNode 列）を Konva ステージに描画する。
// 編集キャンバスと出力の両方がこのレンダラを通ることで、表示と出力の乖離を防ぐ
export function renderSpecToLayer(
  spec: RenderNode[],
  layer: Konva.Layer,
  interactive: boolean,
  callbacks: RendererCallbacks = {},
): void {
  layer.destroyChildren();

  for (const node of spec) {
    if (node.kind === "rect") {
      layer.add(
        new Konva.Rect({
          x: node.rect.x,
          y: node.rect.y,
          width: node.rect.width,
          height: node.rect.height,
          fill: node.fill,
          cornerRadius: node.cornerRadius,
          opacity: node.opacity ?? 1,
          listening: false,
        }),
      );
      continue;
    }

    if (node.kind === "image") {
      const asset = getAsset(node.assetId);
      if (!asset) continue;
      const image = new Konva.Image({
        image: asset,
        x: node.draw.x,
        y: node.draw.y,
        width: node.draw.width,
        height: node.draw.height,
        draggable: interactive,
        name: `slot-${node.slot}`,
      });
      if (interactive) {
        // 選択はポインタを置いた時点で切り替える（タップにもドラッグ開始にも先行する）
        image.on("mousedown touchstart", () => {
          callbacks.onImageSelected?.(node.slot);
        });
        image.on("dragend", () => {
          callbacks.onPlacementChanged?.(node.slot, placementFromImage(node, image));
        });
      }

      if (node.display === "cutout") {
        // 立ち絵方式: クリップ・枠なしでそのまま重ね、グローで背景から浮かせる
        // （canvas の影は透過画像のシルエットに沿って落ちる）
        if (node.glowBlur) {
          image.shadowColor(node.glowColor ?? "#ffffff");
          image.shadowBlur(node.glowBlur);
          image.shadowOpacity(0.9);
        }
        layer.add(image);
        continue;
      }

      const group = new Konva.Group({
        clipFunc: (ctx) => {
          const { x, y, width, height } = node.frame;
          const r = Math.min(node.cornerRadius ?? 0, width / 2, height / 2);
          ctx.beginPath();
          ctx.moveTo(x + r, y);
          ctx.arcTo(x + width, y, x + width, y + height, r);
          ctx.arcTo(x + width, y + height, x, y + height, r);
          ctx.arcTo(x, y + height, x, y, r);
          ctx.arcTo(x, y, x + width, y, r);
          ctx.closePath();
        },
      });
      group.add(image);
      layer.add(group);
      if (node.borderWidth && node.borderColor) {
        layer.add(
          new Konva.Rect({
            x: node.frame.x,
            y: node.frame.y,
            width: node.frame.width,
            height: node.frame.height,
            stroke: node.borderColor,
            strokeWidth: node.borderWidth,
            cornerRadius: node.cornerRadius,
            listening: false,
          }),
        );
      }
      continue;
    }

    const text = new Konva.Text({
      x: node.frame.x,
      y: node.frame.y,
      width: node.frame.width,
      text: node.text,
      fontFamily: node.fontFamily,
      fontSize: node.fontSize,
      fontStyle: node.fontStyle ?? "normal",
      fill: node.fill,
      stroke: node.stroke,
      strokeWidth: node.strokeWidth ?? 0,
      fillAfterStrokeEnabled: true,
      align: node.align,
      opacity: node.opacity ?? 1,
      listening: interactive && node.editable,
      name: node.editable ? `slot-${node.slot}` : undefined,
    });
    if (interactive && node.editable && (node.slot === "viewers" || node.slot === "telop")) {
      const slot = node.slot;
      text.on("click tap", () => callbacks.onTextSlotTapped?.(slot));
    }
    layer.add(text);
  }
}

function placementFromImage(node: ImageNode, image: Konva.Image): Placement {
  const dims = assetDimensions(node.assetId);
  if (!dims) return { scale: 1, offsetX: 0, offsetY: 0 };
  const frame = node.frame;

  if (node.display === "cutout") {
    // 立ち絵は透過素材のため枠の露出がなく、カバー強制（clamp）は行わない。
    // 基準はコンテインフィット・下端アンカー
    const base = Math.min(frame.width / dims.width, frame.height / dims.height);
    const scale = Math.min(Math.max(image.width() / (dims.width * base), 0.2), 8);
    return {
      scale,
      offsetX: image.x() - (frame.x + (frame.width - image.width()) / 2),
      offsetY: image.y() - (frame.y + frame.height - image.height()),
    };
  }

  const base = Math.max(frame.width / dims.width, frame.height / dims.height);
  const scale = image.width() / (dims.width * base);
  const offsetX = image.x() - (frame.x + (frame.width - image.width()) / 2);
  const offsetY = image.y() - (frame.y + (frame.height - image.height()) / 2);
  return clampPlacement(dims, frame, { scale, offsetX, offsetY });
}
